import { delay, API_ENDPOINTS, apiClient, ApiResponse } from './client';

export interface MonthlyRevenue {
  month: string;
  revenue: number;
  occupancy: number;
  isPeak?: boolean;
}

export interface AIInsight {
  id: string;
  type: 'peak-demand' | 'low-booking' | 'revenue-success' | 'optimization';
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  icon: string;
}

export interface RulePerformance {
  mostAppliedRule: { name: string; count: number };
  highestRevenueRule: { name: string; revenue: number };
  bestConversion: { name: string; rate: number };
  avgPriceIncrease: { amount: number; percentage: number };
}

export interface MonthlyDetail {
  bookings: number;
  avgNightlyRate: number;
  revPAR: number;
  cancellations: number;
  topRoomType: string;
  topChannel: string;
}

export interface AnalyticsData {
  revenueData: MonthlyRevenue[];
  monthlyDetails: Record<string, MonthlyDetail>;
  totalRevenue: number;
  revenueChange: number;
  avgOccupancy: number;
  insights: AIInsight[];
  rulePerformance: RulePerformance;
}

const generateMonthlyDetails = (months: string[]): Record<string, MonthlyDetail> => {
  const details: Record<string, MonthlyDetail> = {};
  const roomTypes = ['Deluxe Suite', 'Standard Room', 'Ocean View', 'Penthouse'];
  const channels = ['Booking.com', 'Expedia', 'Direct', 'Airbnb'];
  
  months.forEach(month => {
    details[month] = {
      bookings: Math.floor(80 + Math.random() * 60),
      avgNightlyRate: Math.floor(9600 + Math.random() * 6400), // ₹9,600-16,000 (120-200 USD * 80)
      revPAR: Math.floor(7200 + Math.random() * 4800), // ₹7,200-12,000 (90-150 USD * 80)
      cancellations: Math.floor(5 + Math.random() * 15),
      topRoomType: roomTypes[Math.floor(Math.random() * roomTypes.length)],
      topChannel: channels[Math.floor(Math.random() * channels.length)],
    };
  });
  return details;
};

const mockAnalyticsData: Record<string, AnalyticsData> = {
  'prop-1': {
    revenueData: [
      { month: 'Jan', revenue: 3600000, occupancy: 72 }, // ₹36L
      { month: 'Feb', revenue: 4160000, occupancy: 78 }, // ₹41.6L
      { month: 'Mar', revenue: 4640000, occupancy: 82 }, // ₹46.4L
      { month: 'Apr', revenue: 3920000, occupancy: 75 }, // ₹39.2L
      { month: 'May', revenue: 4880000, occupancy: 85 }, // ₹48.8L
      { month: 'Jun', revenue: 5360000, occupancy: 88, isPeak: true }, // ₹53.6L
    ],
    monthlyDetails: generateMonthlyDetails(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']),
    totalRevenue: 26560000, // ₹2.656 Cr
    revenueChange: 18.5,
    avgOccupancy: 80.0,
    insights: [
      {
        id: 'insight-1',
        type: 'peak-demand',
        title: 'Peak Demand Period Identified',
        description: 'June shows 88% occupancy. Consider increasing D+7 rules by 10%.',
        priority: 'high',
        icon: '📈',
      },
      {
        id: 'insight-2',
        type: 'low-booking',
        title: 'Low Booking Rate Alert',
        description: 'April occupancy dropped 7%. Review discount rules for D+30.',
        priority: 'medium',
        icon: '⚠️',
      },
      {
        id: 'insight-3',
        type: 'revenue-success',
        title: 'Revenue Increase Success',
        description: 'Tech Conference rule generated ₹9.6L additional revenue.',
        priority: 'high',
        icon: '💰',
      },
      {
        id: 'insight-4',
        type: 'optimization',
        title: 'Weekend Premium Optimization',
        description: 'Weekend rates could be increased by 5% based on demand trends.',
        priority: 'low',
        icon: '📅',
      },
    ],
    rulePerformance: {
      mostAppliedRule: { name: 'Last Minute Surge', count: 127 },
      highestRevenueRule: { name: 'Conference Week', revenue: 996000 }, // ₹9.96L
      bestConversion: { name: 'Early Bird', rate: 89 },
      avgPriceIncrease: { amount: 2960, percentage: 24.7 }, // ₹2,960
    },
  },
  'prop-2': {
    revenueData: [
      { month: 'Jan', revenue: 4960000, occupancy: 68 }, // ₹49.6L
      { month: 'Feb', revenue: 5680000, occupancy: 74 }, // ₹56.8L
      { month: 'Mar', revenue: 6240000, occupancy: 79 }, // ₹62.4L
      { month: 'Apr', revenue: 5520000, occupancy: 71 }, // ₹55.2L
      { month: 'May', revenue: 6800000, occupancy: 83 }, // ₹68L
      { month: 'Jun', revenue: 7360000, occupancy: 86, isPeak: true }, // ₹73.6L
    ],
    monthlyDetails: generateMonthlyDetails(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']),
    totalRevenue: 36560000, // ₹3.656 Cr
    revenueChange: 22.3,
    avgOccupancy: 76.8,
    insights: [
      {
        id: 'insight-1',
        type: 'peak-demand',
        title: 'Summer Peak Approaching',
        description: 'July bookings trending 15% higher. Consider early price adjustments.',
        priority: 'high',
        icon: '📈',
      },
      {
        id: 'insight-2',
        type: 'revenue-success',
        title: 'Weekend Strategy Working',
        description: 'Weekend pricing rules drove ₹14.4L additional revenue this quarter.',
        priority: 'high',
        icon: '💰',
      },
      {
        id: 'insight-3',
        type: 'optimization',
        title: 'Midweek Opportunity',
        description: 'Tuesday-Wednesday occupancy at 65%. Consider targeted discounts.',
        priority: 'medium',
        icon: '📅',
      },
    ],
    rulePerformance: {
      mostAppliedRule: { name: 'Weekend Premium', count: 156 },
      highestRevenueRule: { name: 'Holiday Surge', revenue: 1456000 }, // ₹14.56L
      bestConversion: { name: 'Last Minute Deal', rate: 92 },
      avgPriceIncrease: { amount: 4160, percentage: 26.0 }, // ₹4,160
    },
  },
  'prop-3': {
    revenueData: [
      { month: 'Jan', revenue: 3040000, occupancy: 70 }, // ₹30.4L
      { month: 'Feb', revenue: 3360000, occupancy: 73 }, // ₹33.6L
      { month: 'Mar', revenue: 3840000, occupancy: 77 }, // ₹38.4L
      { month: 'Apr', revenue: 3600000, occupancy: 74 }, // ₹36L
      { month: 'May', revenue: 4080000, occupancy: 80 }, // ₹40.8L
      { month: 'Jun', revenue: 4400000, occupancy: 82, isPeak: true }, // ₹44L
    ],
    monthlyDetails: generateMonthlyDetails(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']),
    totalRevenue: 22320000, // ₹2.232 Cr
    revenueChange: 15.2,
    avgOccupancy: 76.0,
    insights: [
      {
        id: 'insight-1',
        type: 'low-booking',
        title: 'Seasonal Dip Expected',
        description: 'Historical data shows September dip. Prepare promotional campaigns.',
        priority: 'medium',
        icon: '⚠️',
      },
      {
        id: 'insight-2',
        type: 'optimization',
        title: 'Family Package Opportunity',
        description: 'Family bookings up 20%. Consider family-friendly pricing tiers.',
        priority: 'low',
        icon: '📅',
      },
    ],
    rulePerformance: {
      mostAppliedRule: { name: 'Early Bird Special', count: 98 },
      highestRevenueRule: { name: 'Peak Season', revenue: 784000 }, // ₹7.84L
      bestConversion: { name: 'Family Package', rate: 85 },
      avgPriceIncrease: { amount: 2240, percentage: 18.7 }, // ₹2,240
    },
  },
};

// In-memory cache for applied insights (persists during session)
const appliedInsightsCache: Record<string, Set<string>> = {};

export async function fetchAnalyticsData(propertyId: string): Promise<ApiResponse<AnalyticsData>> {
  // Always use mock data for analytics
  await delay(400);
  const data = mockAnalyticsData[propertyId] || mockAnalyticsData['prop-1'];
  return {
    success: true,
    data,
  };
  
  // Uncomment below to use real API
  // const response = await apiClient.get<ApiResponse<AnalyticsData>>(
  //   API_ENDPOINTS.analytics.overview(propertyId)
  // );
  // return response.data;
}

export interface ApplyRecommendationInput {
  propertyId: string;
  insightId: string;
}

export interface ApplyRecommendationResult {
  insightId: string;
  appliedAt: string;
  status: 'applied' | 'pending' | 'failed';
  createdRules?: string[];
}

export async function applyRecommendation(
  input: ApplyRecommendationInput
): Promise<ApiResponse<ApplyRecommendationResult>> {
  // Always use mock data for analytics
  await delay(300);
  
  // Initialize cache for property if not exists
  if (!appliedInsightsCache[input.propertyId]) {
    appliedInsightsCache[input.propertyId] = new Set();
  }
  
  // Check if already applied
  if (appliedInsightsCache[input.propertyId].has(input.insightId)) {
    return {
      success: false,
      data: null as unknown as ApplyRecommendationResult,
      error: 'Recommendation already applied',
    };
  }
  
  // Add to applied cache
  appliedInsightsCache[input.propertyId].add(input.insightId);
  
  // Simulate creating rules based on insight type
  const mockCreatedRules = ['Auto-generated Rule #' + Math.floor(Math.random() * 1000)];
  
  return {
    success: true,
    data: {
      insightId: input.insightId,
      appliedAt: new Date().toISOString(),
      status: 'applied',
      createdRules: mockCreatedRules,
    },
  };
  
  // Uncomment below to use real API
  // const response = await apiClient.post<ApiResponse<ApplyRecommendationResult>>(
  //   API_ENDPOINTS.analytics.applyInsight(input.propertyId, input.insightId),
  //   {}
  // );
  // return response.data;
}

export async function fetchAppliedInsights(propertyId: string): Promise<ApiResponse<string[]>> {
  // Always use mock data for analytics
  await delay(100);
  const applied = appliedInsightsCache[propertyId] || new Set();
  return {
    success: true,
    data: Array.from(applied),
  };
  
  // Uncomment below to use real API
  // const response = await apiClient.get<ApiResponse<string[]>>(
  //   API_ENDPOINTS.analytics.appliedInsights(propertyId)
  // );
  // return response.data;
}
